import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import PageTilesGrid from "../components/PageTilesGrid";
import { api } from "../lib/api";
import { POST_HTML_PURIFY_CONFIG, sanitizeIframeHook, sanitizeAnchorAttrHook, flagSmallTextParagraphs } from "../lib/sanitize";
import RichBodyWithCarousels from "../components/RichBodyWithCarousels";
import { ShareButton } from "../components/ShareButton";

// Register the iframe allowlist hook once at module load.
DOMPurify.addHook("uponSanitizeElement", sanitizeIframeHook);
// Preserve admin-chosen `target` + `rel` on <a> tags (DOMPurify strips
// them by default).
DOMPurify.addHook("uponSanitizeAttribute", sanitizeAnchorAttrHook);

export default function CustomPage({ slug: explicitSlug } = {}) {
  const { slug: urlSlug } = useParams();
  const slug = explicitSlug || urlSlug;
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setPage(null);
    setError(null);
    api
      .get(`/custom-pages/${slug}`)
      .then((r) => alive && setPage(r.data))
      .catch((e) =>
        alive && setError(e?.response?.status === 404 ? "notfound" : "error")
      );
    return () => { alive = false; };
  }, [slug]);

  if (error === "notfound") return <Navigate to="/" replace />;

  const sanitizeHtml = (raw) =>
    flagSmallTextParagraphs(DOMPurify.sanitize(raw || "", POST_HTML_PURIFY_CONFIG));

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]" data-testid="page-custom">
      <Header />

      <main className="mx-auto max-w-7xl px-5 pt-4 pb-8 sm:px-8 sm:pt-10 sm:pb-10">
        {page && !page.hide_hero ? (
          <div className="border-b border-black/10 pb-6" data-testid="custom-hero">
            <div className="flex items-center justify-between gap-3">
              <div
                className="flex items-center gap-2 text-brand-accent"
                style={page.kicker_color ? { color: page.kicker_color } : undefined}
              >
                {page.kicker ? <span className="label-kicker">{page.kicker}</span> : null}
              </div>
              <ShareButton
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={page.title}
              />
            </div>
            <h1
              className="brand-headline mt-3 whitespace-pre-line font-display text-4xl font-black tracking-tight sm:text-5xl"
              style={page.title_color ? { color: page.title_color } : undefined}
            >
              {page.title}
            </h1>
            {page.intro ? (
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-[var(--brand-body)]">
                {page.intro}
              </p>
            ) : null}
          </div>
        ) : null}

        {!page ? (
          <div className="mt-10 flex items-center gap-2 text-[var(--brand-body)]">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : page.body ? (
          <RichBodyWithCarousels
            html={page.body}
            sanitize={sanitizeHtml}
            className="prose prose-slate mt-10 max-w-none rounded-md border border-black/10 bg-white p-6 sm:p-10 prose-headings:font-display prose-a:text-brand-accent"
            testId="custom-body"
          />
        ) : (page.empty_title || page.empty_body) ? (
          <div
            className="mt-10 rounded-md border border-dashed border-black/15 p-10 text-center"
            data-testid="custom-empty"
          >
            {page.empty_title ? (
              <p className="font-display text-lg font-bold">{page.empty_title}</p>
            ) : null}
            {page.empty_body ? (
              <p className="mt-1 text-sm text-[var(--brand-body)]">
                {page.empty_body}
              </p>
            ) : null}
          </div>
        ) : null}
      </main>

      {page ? (
        <section className="mx-auto max-w-6xl px-5 pb-16 sm:px-8 sm:pb-20">
          <PageTilesGrid
            pageKey={`page:${slug}`}
            kicker=""
            heading=""
          />
        </section>
      ) : null}

      <Footer />
    </div>
  );
}
